import { faBars } from '@fortawesome/pro-solid-svg-icons/faBars';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Colour,
	FlexGap,
	ListItem,
	ListItemGroup,
	ListItemSize,
	StackVertical,
	useLayout,
	useOutsiderDetector,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { AccountHeader } from '../../Account/Components/AccountHeader';
import { useSite } from '../../_Global/Providers/SiteInterfaceProvider';
import { AccountRoutes } from '../Routes/AccountRoutes';
import { PaymentRoutes } from '../Routes/PaymentRoutes';
import { ReferralRoutes } from '../Routes/ReferralRoutes';
import { SecurityRoutes } from '../Routes/SecurityRoutes';
import { SiteSettingsRoutes } from '../Routes/SiteSettingsRoutes';
import { YourPlanRoutes } from '../Routes/YourPlanRoutes';

function AccountOverview() {
	const navigate = useNavigate();
	const location = useLocation();

	const { t } = useTranslation();
	const { isLarge } = useLayout();
	const { currentLocale, updatePageMeta } = useSite();

	const [listOpen, setListOpen] = useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState<string>(location.pathname.replace('/manage-account', ''));

	const containerRef = useRef<any>(null);
	const listRef = useRef<HTMLDivElement>(null);

	useOutsiderDetector(listRef, () => setListOpen(false));

	const HandleNavigation = (path: string) => {
		navigate(path);
		setListOpen(false);
	};

	useEffect(() => {
		updatePageMeta({
			title: t('actions.manageAccount'),
		});
		setCurrentPage(location.pathname.replace('/manage-account', ''));
	}, [currentLocale, location]);

	return (
		<div className='content-main content-area-narrow ' ref={containerRef}>
			<div className={`container-narrow pb-sm ${isLarge || currentPage === '' ? 'pt-sm' : ''}`}>
				<StackVertical gap={FlexGap.ExtraLarge}>
					{(isLarge || currentPage === '') && <AccountHeader inOverview />}

					<div className={`account-overview-container ${currentPage ? 'active' : ''}`}>
						<div className={`account-overview-navigation sticky ${listOpen ? 'open' : ''} `} ref={listRef}>
							{!isLarge && currentPage && (
								<div
									className='account-info-trigger'
									onClick={() => setListOpen(!listOpen)}
									onKeyDown={(e) => (e.key === 'Enter' ? setListOpen(!listOpen) : undefined)}
									role='button'
									tabIndex={0}
								>
									<FontAwesomeIcon
										icon={listOpen ? faXmark : faBars}
										style={{ marginInlineEnd: '.75em' }}
									/>
									<h6 className='m-0 sub-heading'>{t('actions.manageAccount')}</h6>
								</div>
							)}

							<div className='account-info-list'>
								<StackVertical extraClass='account-info-group'>
									<div className='account-info-meta'>
										<div className='account-info-section-title'>
											{t(`settings.groupHeadings.${YourPlanRoutes[0].group}`)}
										</div>
									</div>
									<ListItemGroup>
										{YourPlanRoutes.map((item) => (
											<ListItem
												key={`routePlan${item.title}`}
												title={t(`settings.sections.${item.title}`)}
												iconContent={{
													icon: item.icon,
													iconBg: item.colour || Colour.Warning,
												}}
												onClick={() => HandleNavigation(item.path)}
												rounded={!!currentPage}
												hideSeparator={!!currentPage}
												size={currentPage ? ListItemSize.ExtraSmall : ListItemSize.Medium}
											/>
										))}
									</ListItemGroup>
								</StackVertical>

								<StackVertical extraClass='account-info-group'>
									<div className='account-info-meta'>
										<div className='account-info-section-title'>
											{t(`settings.groupHeadings.${AccountRoutes[0].group}`)}
										</div>
									</div>
									<ListItemGroup>
										{AccountRoutes.map((item) => (
											<ListItem
												key={`routePlan${item.title}`}
												title={t(`settings.sections.${item.title}`)}
												iconContent={{
													icon: item.icon,
													iconBg: item.colour || Colour.Warning,
												}}
												onClick={() => HandleNavigation(item.path)}
												rounded={!!currentPage}
												hideSeparator={!!currentPage}
												size={currentPage ? ListItemSize.ExtraSmall : ListItemSize.Medium}
											/>
										))}
									</ListItemGroup>
								</StackVertical>

								<StackVertical extraClass='account-info-group'>
									<div className='account-info-meta'>
										<div className='account-info-section-title'>
											{t(`settings.groupHeadings.${PaymentRoutes[0].group}`)}
										</div>
									</div>
									<ListItemGroup>
										{PaymentRoutes.map((item) => (
											<ListItem
												key={`routePlan${item.title}`}
												title={t(`settings.sections.${item.title}`)}
												iconContent={{
													icon: item.icon,
													iconBg: item.colour || Colour.Warning,
												}}
												onClick={() => HandleNavigation(item.path)}
												rounded={!!currentPage}
												hideSeparator={!!currentPage}
												size={currentPage ? ListItemSize.ExtraSmall : ListItemSize.Medium}
											/>
										))}
									</ListItemGroup>
								</StackVertical>

								<StackVertical extraClass='account-info-group'>
									<div className='account-info-meta'>
										<div className='account-info-section-title'>
											{t(`settings.groupHeadings.${SecurityRoutes[0].group}`)}
										</div>
									</div>
									<ListItemGroup>
										{SecurityRoutes.map((item) => (
											<ListItem
												key={`routePlan${item.title}`}
												title={t(`settings.sections.${item.title}`)}
												iconContent={{
													icon: item.icon,
													iconBg: item.colour || Colour.Warning,
												}}
												onClick={() => HandleNavigation(item.path)}
												rounded={!!currentPage}
												hideSeparator={!!currentPage}
												size={currentPage ? ListItemSize.ExtraSmall : ListItemSize.Medium}
											/>
										))}
									</ListItemGroup>
								</StackVertical>

								<StackVertical extraClass='account-info-group'>
									<div className='account-info-meta'>
										<div className='account-info-section-title'>
											{t(`settings.groupHeadings.${ReferralRoutes[0].group}`)}
										</div>
									</div>
									<ListItemGroup>
										{ReferralRoutes.map((item) => (
											<ListItem
												key={`routePlan${item.title}`}
												title={t(`settings.sections.${item.title}`)}
												iconContent={{
													icon: item.icon,
													iconBg: item.colour || Colour.Warning,
												}}
												onClick={() => HandleNavigation(item.path)}
												rounded={!!currentPage}
												hideSeparator={!!currentPage}
												size={currentPage ? ListItemSize.ExtraSmall : ListItemSize.Medium}
											/>
										))}
									</ListItemGroup>
								</StackVertical>

								<StackVertical extraClass='account-info-group'>
									<div className='account-info-meta'>
										<div className='account-info-section-title'>
											{t(`settings.groupHeadings.${SiteSettingsRoutes[0].group}`)}
										</div>
									</div>
									<ListItemGroup>
										{SiteSettingsRoutes.map((item) => (
											<ListItem
												key={`routePlan${item.title}`}
												title={t(`settings.sections.${item.title}`)}
												iconContent={{
													icon: item.icon,
													iconBg: item.colour || Colour.Warning,
												}}
												onClick={() => HandleNavigation(item.path)}
												rounded={!!currentPage}
												hideSeparator={!!currentPage}
												size={currentPage ? ListItemSize.ExtraSmall : ListItemSize.Medium}
											/>
										))}
									</ListItemGroup>
								</StackVertical>
							</div>
						</div>
						<div className='account-overview-content'>
							<Outlet />
						</div>
					</div>

					<div className='faded text-center'>
						{process.env.REACT_APP_NAME} - {process.env.REACT_APP_VERSION}
					</div>
				</StackVertical>
			</div>
		</div>
	);
}

export default AccountOverview;
